import React from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Email,
  Phone,
  CalendarToday,
  AttachMoney,
  WorkOutline,
  LocationOn,
  AccessTime,
  CheckCircle,
  Description,
  Assignment,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

// Mock data - replace with actual API call
const staffData = {
  'S001': {
    id: 'S001',
    name: 'Michael Green',
    role: 'Manager',
    department: 'Management',
    email: 'michael@hotelops.com',
    phone: '555-100-1001',
    salary: 5500,
    status: 'Active',
    joinDate: '2022-01-15',
    shift: 'Day',
    address: '123 Main Street, New York, NY 10001',
    emergencyContact: 'Jane Green - 555-200-2001',
    dateOfBirth: '1985-05-20',
    employeeType: 'Full-time',
    performanceRating: 4.8,
    totalTasks: 245,
    completedTasks: 238,
    attendanceRate: 98,
    leaveBalance: 12,
  },
  'S002': {
    id: 'S002',
    name: 'Sara White',
    role: 'Receptionist',
    department: 'Front Desk',
    email: 'sara@hotelops.com',
    phone: '555-100-1002',
    salary: 2800,
    status: 'Active',
    joinDate: '2023-03-20',
    shift: 'Day',
    address: '456 Oak Avenue, New York, NY 10002',
    emergencyContact: 'John White - 555-200-2002',
    dateOfBirth: '1992-08-15',
    employeeType: 'Full-time',
    performanceRating: 4.5,
    totalTasks: 189,
    completedTasks: 180,
    attendanceRate: 95,
    leaveBalance: 8,
  },
  'S003': {
    id: 'S003',
    name: 'Tom Black',
    role: 'Housekeeping',
    department: 'Housekeeping',
    email: 'tom@hotelops.com',
    phone: '555-100-1003',
    salary: 2200,
    status: 'On Leave',
    joinDate: '2023-06-10',
    shift: 'Day',
    address: '789 Pine Road, New York, NY 10003',
    emergencyContact: 'Mary Black - 555-200-2003',
    dateOfBirth: '1988-11-30',
    employeeType: 'Full-time',
    performanceRating: 4.2,
    totalTasks: 156,
    completedTasks: 145,
    attendanceRate: 92,
    leaveBalance: 3,
  },
  'S004': {
    id: 'S004',
    name: 'Lisa Brown',
    role: 'Chef',
    department: 'Kitchen',
    email: 'lisa@hotelops.com',
    phone: '555-100-1004',
    salary: 3500,
    status: 'Active',
    joinDate: '2022-08-05',
    shift: 'Day',
    address: '321 Elm Street, New York, NY 10004',
    emergencyContact: 'Mike Brown - 555-200-2004',
    dateOfBirth: '1990-03-12',
    employeeType: 'Full-time',
    performanceRating: 4.9,
    totalTasks: 312,
    completedTasks: 308,
    attendanceRate: 99,
    leaveBalance: 15,
  },
  'S005': {
    id: 'S005',
    name: 'James Wilson',
    role: 'Security',
    department: 'Security',
    email: 'james@hotelops.com',
    phone: '555-100-1005',
    salary: 2600,
    status: 'Active',
    joinDate: '2023-11-12',
    shift: 'Night',
    address: '654 Maple Drive, New York, NY 10005',
    emergencyContact: 'Sarah Wilson - 555-200-2005',
    dateOfBirth: '1987-07-25',
    employeeType: 'Full-time',
    performanceRating: 4.6,
    totalTasks: 98,
    completedTasks: 95,
    attendanceRate: 97,
    leaveBalance: 10,
  },
  'S006': {
    id: 'S006',
    name: 'Emma Davis',
    role: 'Receptionist',
    department: 'Front Desk',
    email: 'emma@hotelops.com',
    phone: '555-100-1006',
    salary: 2800,
    status: 'Active',
    joinDate: '2024-02-18',
    shift: 'Night',
    address: '987 Cedar Lane, New York, NY 10006',
    emergencyContact: 'David Davis - 555-200-2006',
    dateOfBirth: '1995-01-08',
    employeeType: 'Full-time',
    performanceRating: 4.4,
    totalTasks: 67,
    completedTasks: 64,
    attendanceRate: 96,
    leaveBalance: 14,
  },
};

// Mock recent activities
const recentActivities = [
  { date: '2025-11-15', activity: 'Completed monthly report', type: 'Task' },
  { date: '2025-11-10', activity: 'Attended team meeting', type: 'Meeting' },
  { date: '2025-11-05', activity: 'Customer service training', type: 'Training' },
  { date: '2025-10-28', activity: 'Performance review completed', type: 'Review' },
  { date: '2025-10-20', activity: 'Shift coverage for colleague', type: 'Task' },
];

// Mock attendance history
const attendanceHistory = [
  { date: '2025-11-15', checkIn: '09:00 AM', checkOut: '05:00 PM', status: 'Present', hours: 8 },
  { date: '2025-11-14', checkIn: '09:05 AM', checkOut: '05:02 PM', status: 'Present', hours: 8 },
  { date: '2025-11-13', checkIn: '08:58 AM', checkOut: '05:00 PM', status: 'Present', hours: 8 },
  { date: '2025-11-12', checkIn: '09:00 AM', checkOut: '05:00 PM', status: 'Present', hours: 8 },
  { date: '2025-11-11', checkIn: '-', checkOut: '-', status: 'Leave', hours: 0 },
];

export default function StaffDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const staff = staffData[id] || staffData['S001']; // Fallback to S001 if ID not found

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'On Leave': return 'warning';
      case 'Inactive': return 'default';
      default: return 'default';
    }
  };

  const getAttendanceStatusColor = (status) => {
    switch (status) {
      case 'Present': return 'success';
      case 'Leave': return 'warning';
      case 'Absent': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/staff')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Staff Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete information about staff member
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Edit />}>
          Edit Profile
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <Avatar sx={{ width: 120, height: 120, bgcolor: '#1976d2', fontSize: '3rem', mb: 2 }}>
                {staff.name[0]}
              </Avatar>
              <Typography variant="h5" fontWeight="bold" align="center">
                {staff.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {staff.role}
              </Typography>
              <Chip
                label={staff.status}
                color={getStatusColor(staff.status)}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <List dense>
              <ListItem>
                <Email sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText primary="Email" secondary={staff.email} />
              </ListItem>
              <ListItem>
                <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText primary="Phone" secondary={staff.phone} />
              </ListItem>
              <ListItem>
                <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText primary="Join Date" secondary={staff.joinDate} />
              </ListItem>
              <ListItem>
                <WorkOutline sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText primary="Department" secondary={staff.department} />
              </ListItem>
              <ListItem>
                <AccessTime sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText primary="Shift" secondary={staff.shift} />
              </ListItem>
              <ListItem>
                <AttachMoney sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText primary="Salary" secondary={`$${staff.salary.toLocaleString()}/month`} />
              </ListItem>
            </List>
          </Paper>

          {/* Additional Info */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Date of Birth" 
                  secondary={staff.dateOfBirth}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Employee Type" 
                  secondary={staff.employeeType}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Address" 
                  secondary={staff.address}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Emergency Contact" 
                  secondary={staff.emergencyContact}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Performance Stats */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Rating
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#1976d2">
                    {staff.performanceRating}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    out of 5.0
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Tasks
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#2e7d32">
                    {staff.completedTasks}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    of {staff.totalTasks}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Attendance
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#ed6c02">
                    {staff.attendanceRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    this year
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Leave
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#9c27b0">
                    {staff.leaveBalance}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    days left
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Activities */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Assignment sx={{ mr: 1, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="bold">
                Recent Activities
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <List>
              {recentActivities.map((activity, index) => (
                <ListItem key={index} divider={index < recentActivities.length - 1}>
                  <CheckCircle sx={{ mr: 2, color: '#2e7d32' }} fontSize="small" />
                  <ListItemText
                    primary={activity.activity}
                    secondary={activity.date}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Chip label={activity.type} size="small" variant="outlined" />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Attendance History */}
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <CalendarToday sx={{ mr: 1, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="bold">
                Attendance History
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Check-in</strong></TableCell>
                    <TableCell><strong>Check-out</strong></TableCell>
                    <TableCell><strong>Hours</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceHistory.map((record, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{record.date}</TableCell>
                      <TableCell>{record.checkIn}</TableCell>
                      <TableCell>{record.checkOut}</TableCell>
                      <TableCell>{record.hours}h</TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          size="small"
                          color={getAttendanceStatusColor(record.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
